import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, User, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    document.title = "Blog - Scaffolding Insights & Guides | Amar Preet";
    axios.get(`${API}/blog`)
      .then(res => setPosts(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filteredPosts = activeCategory === "all" 
    ? posts 
    : posts.filter(p => p.category === activeCategory);

  const categories = [
    { id: "all", name: "All Posts" },
    { id: "commercial", name: "Commercial" },
    { id: "city", name: "City Guides" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="blog-page">
      <SEO title="Blog" description="Expert insights on scaffolding rental, safety standards, cost guides and construction tips from Amar Preet Scaffolding. Read our latest articles." canonical="/blog" />
      {/* Hero */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-yellow-500 font-semibold text-sm tracking-wider uppercase">Blog</span>
          <h1 className="text-5xl md:text-6xl font-bold mt-4 mb-6" data-testid="blog-headline">
            Scaffolding Insights & Guides
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Expert articles on scaffolding rental, construction best practices, and industry insights
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeCategory === cat.id 
                    ? "bg-yellow-500 text-slate-900" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`category-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article 
                  key={post.id} 
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
                  data-testid={`blog-card-${post.slug}`}
                >
                  {post.featured_image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  )}
                  
                  <div className="p-6">
                    <span className="inline-block text-xs font-semibold uppercase tracking-wider px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full mb-4">
                      {post.category}
                    </span>
                    
                    <h2 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-yellow-600 transition-colors">
                      <Link to={`/blog/${post.slug}`}>
                        {post.title}
                      </Link>
                    </h2>
                    
                    <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                      {post.meta_description}
                    </p>
                    
                    <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.created_at).toLocaleDateString('en-IN', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {post.author}
                      </span>
                    </div>
                    
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-yellow-600 font-semibold hover:text-yellow-700 transition-colors"
                    >
                      Read More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-slate-600">No posts found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Need Expert Advice?</h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            Our team can help you choose the right scaffolding solution for your project
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-8 py-4 rounded-lg font-semibold transition-all duration-300 hover:scale-105"
            data-testid="blog-cta"
          >
            Talk to an Expert
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
